import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

//css
import { Button, Table, Modal, Spinner, Form, FloatingLabel, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faEdit } from "@fortawesome/free-solid-svg-icons";

// Components
import AlertModal from "../../components/AlertModal";

// Pages
import AddSSMembers from "./AddSSMembers";
import AppPagination from "../components/Pagination";
import AppNav from "../../header/AppNav";
import { SSMemberServices } from "../../services/SSMemberService";
import { validateEmail } from "../../utils/validations";
import styles from "../../styles/Modal.module.css";
import buttonStyles from "../../styles/Buttons.module.css";

function SSMembers() {
  const [addModalShow, setAddModalShow] = useState(false);
  const [ssMembers, setSSMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const totalPages = Math.ceil(ssMembers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = ssMembers.slice(indexOfFirstItem, indexOfLastItem);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0); 
  };
  
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [modalData, setModalData] = useState({});

  const [editFormData, setEditFormData] = useState({
    id: '',
    name: '',
    mobileNo: '',
    email: '',
    designation: '',
    referalHospital: ''
  });

  useEffect(() => {
    getSSMembers();
  }, []);

  const getSSMembers = async () => {
    try {
      setLoading(true);
      const response = await SSMemberServices.getAllSSMembers();
      console.log('API Response:', response);
      
     
      if (Array.isArray(response)) {
        setSSMembers(response);
      } 
    
      else if (response && Array.isArray(response.data)) {
        setSSMembers(response.data);
      }
    
      else if (response && response.status === 200 && Array.isArray(response.data)) {
        setSSMembers(response.data);
      } 
     
      else if (response && typeof response === 'object') {
        const members = Object.values(response).filter(item => 
          item && typeof item === 'object' && 'id' in item
        );
        setSSMembers(members);
      }
      else {
        setSSMembers([]);
        setError("No valid member data received");
      }
    } catch (err) {
      console.error("Error fetching SS Members:", err);
      setError("Failed to load SS Members. Please try again later.");
      setSSMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (member) => {
    setEditFormData({
      id: member.id,
      name: member.name || '',
      mobileNo: member.mobileNo || '',
      email: member.email || '',
      designation: member.designation || '',
      referalHospital: member.referalHospital || ''
    });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { id, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (editFormData.email && !validateEmail(editFormData.email)) {
      setModalData({
        title: 'Invalid Email',
        message: 'Please enter a valid email address (e.g., example@domain.com)',
        variant: 'warning'
      });
      setShowAlertModal(true);
      return;
    }
    
    setLoading(true);
    
    try {
      const memberData = {
        id: editFormData.id,
        name: editFormData.name.trim(),
        mobileNo: editFormData.mobileNo.trim(),
        email: editFormData.email ? editFormData.email.trim() : null,
        designation: editFormData.designation.trim(),
        referalHospital: editFormData.referalHospital || null
      };

      const response = await SSMemberServices.updateSSMember(memberData);
      
      if (response && (response.status === 200 || response.status === 201)) {
        // Close the edit modal first
        setShowEditModal(false);
        
        // Show success message
        setModalData({
          title: 'Success',
          message: 'Member updated successfully!',
          variant: 'success'
        });
        setShowAlertModal(true);
        
        // Refresh the list
        getSSMembers();
      } else {
        throw new Error(response?.data?.message || 'Failed to update member');
      }
    } catch (error) {
      console.error('Error updating member:', error);
      // Close the edit modal first
      setShowEditModal(false);
      
      // Show error message
      setModalData({
        title: 'Error',
        message: error.message || 'Failed to update member. Please try again.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <AppNav />
      <h1 className="list-style">Secondary Screening Members</h1>
      <div className="list-table-style">
        <div className="add-button mb-3">
          <Button 
            variant="primary" 
            onClick={() => setAddModalShow(true)}
            className={buttonStyles.primaryButton}
          >
            Add Member
          </Button>
          <AddSSMembers
            show={addModalShow}
            onHide={() => setAddModalShow(false)}
            onClose={getSSMembers}
          />
        </div>

        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading members...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            {error}
            <Button 
              variant="link" 
              onClick={getSSMembers} 
              className="p-0 ms-2"
              style={{ boxShadow: 'none' }}
            >
              Retry
            </Button>
          </div>
        ) : ssMembers.length === 0 ? (
          <div className="text-center p-5">
            <p>No members found.</p>
          </div>
        ) : (
          <div>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Sl No</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Designation</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((member, index) => (
                  <tr key={member.id || index}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td>{member.name || 'N/A'}</td>
                    <td>{member.mobileNo || 'N/A'}</td>
                    <td>{member.email || 'N/A'}</td>
                    <td>{member.designation || 'N/A'}</td>
                    <td className="text-center">
                      <Button
                        variant="link"
                        className="text-primary p-0"
                        onClick={() => handleEditClick(member)}
                        title="Edit Member"
                        style={{ boxShadow: 'none' }}
                      >
                        <FontAwesomeIcon icon={faEdit} style={{ color: '#05275a' }} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {ssMembers.length > itemsPerPage && (
              <div className="d-flex justify-content-center mt-4">
                <AppPagination 
                  curPage={currentPage} 
                  maxPage={totalPages} 
                  changePage={handlePageChange} 
                />
              </div>
            )}
          </div>
        )}
      </div>
      {/* Pagination can be added back when the API supports it */}


      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => !loading && setShowEditModal(false)}
        size="lg"
        centered
        className={styles.modalShow}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>Edit Member</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body className={styles.modalBody}>
            {loading && (
              <div className="text-center p-3">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Updating...</span>
                </Spinner>
                <p className="mt-2">Updating member details...</p>
              </div>
            )}
            {!loading && (
              <>
                <Row>
                  <Col>
                    <FloatingLabel controlId="name" label="Name*" className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Name*"
                        value={editFormData.name}
                        onChange={handleEditChange}
                        required
                      />
                    </FloatingLabel>
                  </Col>
                  <Col>
                    <FloatingLabel controlId="mobileNo" label="Mobile No*" className="mb-3">
                      <Form.Control
                        type="tel"
                        placeholder="Mobile No*"
                        value={editFormData.mobileNo}
                        onChange={handleEditChange}
                        required
                        pattern="[0-9]{10}"
                        title="Please enter a valid 10-digit mobile number"
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FloatingLabel controlId="email" label="Email" className="mb-3">
                      <Form.Control
                        type="email"
                        placeholder="Email"
                        value={editFormData.email}
                        onChange={handleEditChange}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col>
                    <FloatingLabel controlId="designation" label="Designation*" className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Designation*"
                        value={editFormData.designation}
                        onChange={handleEditChange}
                        required
                      />
                    </FloatingLabel>
                  </Col>
                  <Col>
                    <FloatingLabel controlId="referalHospital" label="Hospital" className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Hospital"
                        value={editFormData.referalHospital}
                        onChange={handleEditChange}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>
          <hr className="color-grey" />
          <Modal.Footer className={styles.modalFooter}>
            <Button
              variant="success"
              type="submit"
              disabled={loading}
              className={buttonStyles.primaryButton}
            >
              {loading ? 'Updating...' : 'Update'}
            </Button>
            <Button
              variant="danger"
              onClick={() => setShowEditModal(false)}
              className={buttonStyles.dangerButton}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Alert Modal */}
      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={modalData.title}
        message={modalData.message}
        variant={modalData.variant || 'info'}
      />
    </div>
  );
}

export default SSMembers;
